import { SNOW_FLAKE_FLOATS, SNOW_MAX_FLAKES, SNOW_QUAD_INDICES, type SnowBasis, type SnowField } from "../snowfield";
import type { ViewProjection } from "../window";
import { createBuffer, createVertexArray, describeAttributes } from "./buffer";
import { checkGlError } from "./context";
import { createProgram, uniformLocations, type Uniforms } from "./shader";

const SNOW_VERTEX_SOURCE = `#version 300 es
precision highp float;
in vec3 a_position;
in vec2 a_uv;
uniform mat4 u_viewProj;
out vec2 v_uv;
void main() {
  gl_Position = u_viewProj * vec4(a_position, 1.0);
  v_uv = a_uv;
}
`;

// Colour and alpha both select the texture (`SnowEnvironment.cpp:213-216`).
const SNOW_FRAGMENT_SOURCE = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_texture;
out vec4 fragColour;
void main() {
  fragColour = texture(u_texture, v_uv);
}
`;

const SNOW_UNIFORMS = ["u_viewProj", "u_texture"] as const;

/** `CSnowEnvironment::Render`: every flake of a `SnowField` as a camera-facing
 *  quad in the depth-tested scene, alpha-blended, writing no depth and culling
 *  no face (`SnowEnvironment.cpp:172-231`). */
export class GlSnow {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof SNOW_UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly vbo: WebGLBuffer;
  private readonly vertices = new Float32Array(SNOW_MAX_FLAKES * 4 * SNOW_FLAKE_FLOATS);

  constructor(
    private readonly gl: WebGL2RenderingContext,
    private readonly texture: WebGLTexture,
  ) {
    this.program = createProgram(gl, SNOW_VERTEX_SOURCE, SNOW_FRAGMENT_SOURCE, "snow");
    this.uniforms = uniformLocations(gl, this.program, SNOW_UNIFORMS);
    this.vao = createVertexArray(gl, "snow");
    gl.bindVertexArray(this.vao);
    this.vbo = createBuffer(gl, "snow flakes");
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.vertices.byteLength, gl.DYNAMIC_DRAW);
    describeAttributes(gl, this.program, SNOW_FLAKE_FLOATS * 4, [
      { name: "a_position", components: 3, type: gl.FLOAT, normalized: false, offset: 0, divisor: 0 },
      { name: "a_uv", components: 2, type: gl.FLOAT, normalized: false, offset: 12, divisor: 0 },
    ]);
    const indices = new Uint16Array(SNOW_MAX_FLAKES * 6);
    for (let i = 0; i < SNOW_MAX_FLAKES; i++) {
      for (let j = 0; j < 6; j++) indices[i * 6 + j] = i * 4 + SNOW_QUAD_INDICES[j];
    }
    const ibo = createBuffer(gl, "snow indices");
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);
    gl.bindVertexArray(null);
    checkGlError(gl, "snow upload");
  }

  draw(view: ViewProjection, field: SnowField, basis: SnowBasis): void {
    const flakes = field.writeQuads(this.vertices, basis);
    if (flakes === 0) return;
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.vertices, 0, flakes * 4 * SNOW_FLAKE_FLOATS);
    gl.uniformMatrix4fv(this.uniforms.u_viewProj, false, view.viewProj);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.uniform1i(this.uniforms.u_texture, 0);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.disable(gl.CULL_FACE);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.drawElements(gl.TRIANGLES, flakes * 6, gl.UNSIGNED_SHORT, 0);
    gl.disable(gl.BLEND);
    gl.depthMask(true);
    gl.bindVertexArray(null);
  }
}
